import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { sendEmailVerification } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";

export default function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email; // Get email passed from signup

  const [isVerifying, setIsVerifying] = useState(true); // Check verification status initially
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [canResend, setCanResend] = useState(false);
  const [timer, setTimer] = useState(60); // Timer for resend button

  const intervalRef = useRef(null); // Ref to hold the verification check interval
  const timerRef = useRef(null); // Ref to hold the resend timer interval

  // Function to start the resend timer
  const startResendTimer = () => {
    setCanResend(false);
    setTimer(60);
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Effect to check verification status periodically
  useEffect(() => {
    if (!email) {
      setError("Email address not found. Please sign up again.");
      setIsVerifying(false);
      // Optional: Redirect back to signup after a delay
      // setTimeout(() => navigate('/signup'), 3000);
      return;
    }

    // Immediately start the resend timer when the component mounts
    startResendTimer();

    setMessage(
      `A verification link has been sent to ${email}. Please check your inbox (and spam folder). This page will automatically redirect after verification.`
    );

    intervalRef.current = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          await currentUser.reload(); // Force refresh user state from Firebase
          console.log(
            "Checking email verification status:",
            currentUser.emailVerified
          );
          if (currentUser.emailVerified) {
            clearInterval(intervalRef.current); // Stop checking
            clearInterval(timerRef.current); // Stop timer
            console.log("Email verified successfully!");

            // Update Firestore emailVerified field in the correct collection
            try {
              const userType = localStorage.getItem("userType");
              const collection = userType === "client" ? "client" : "lawyers";
              const userRef = doc(db, collection, currentUser.uid);
              await updateDoc(userRef, { emailVerified: true });
            } catch (firestoreErr) {
              console.error("Error updating emailVerified in Firestore:", firestoreErr);
            }

            navigate("/portal/PhoneVerification");
          }
        } catch (reloadError) {
          // This can happen if the user token expired or they logged out elsewhere
          console.error("Error reloading user:", reloadError);
          setError(
            "Could not verify email status. Please try logging in again."
          );
          clearInterval(intervalRef.current);
          clearInterval(timerRef.current);
          setIsVerifying(false);
          // Optional: Redirect to login
          // setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        // This case might happen if the user manually logs out or the session ends unexpectedly
        console.warn("No user found while checking verification status.");
        setError("You seem to be logged out. Please log in again.");
        clearInterval(intervalRef.current);
        clearInterval(timerRef.current);
        setIsVerifying(false);
        // Optional: Redirect to login
        // setTimeout(() => navigate('/login'), 3000);
      }
    }, 5000); // Check every 5 seconds

    // Cleanup function to clear intervals when component unmounts
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(timerRef.current);
    };
  }, [email, navigate]); // Rerun effect if email or navigate changes (though email shouldn't change here)

  const handleResendEmail = async () => {
    if (!canResend) return; // Don't allow resend if timer is active

    setError("");
    setMessage("");
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("Could not resend email. User not found. Please log in.");
      return;
    }

    try {
      await sendEmailVerification(currentUser);
      setMessage(`A new verification link has been sent to ${email}.`);
      startResendTimer(); // Restart the timer
    } catch (err) {
      console.error("Error resending verification email:", err);
      setError(err.message || "Failed to resend verification email.");
      // Handle specific errors like 'auth/too-many-requests'
      if (err.code === "auth/too-many-requests") {
        setError(
          "Too many resend requests. Please wait a while before trying again."
        );
        // Optionally disable resend for longer or provide specific instructions
        setCanResend(false); // Keep disabled if too many requests
      }
    }
  };

  // ---- Render component ----
  // Removed images for brevity, add them back as needed
  return (
    <div className="min-h-screen overflow-hidden py-10 sm:py-0 flex flex-col bg-[#060223] items-center justify-center relative text-white">
      {/* Add back your image tags here if needed */}
      {/* <img src={ellipseTop} className='absolute ...' alt="" /> */}
      {/* ... other images */}

      <div className="flex flex-col gap-5 items-center p-5 z-101 rounded-lg w-full max-w-md">
        {/* <img src={emailVerificationIcon} alt="" className='w-[100px]' /> */}
        <h2 className="text-4xl sm:text-5xl font-semibold text-[#C99F4A] text-center">
          Verify Your Email
        </h2>

        {email ? (
          <p className="text-center text-gray-300 mt-2 text-sm px-4">
            {message ||
              `Please click the verification link sent to ${email}. Check your spam folder if you don't see it.`}
          </p>
        ) : (
          <p className="text-center text-red-400 mt-2 text-sm px-4">
            Loading email information or email not provided...
          </p>
        )}

        {error && (
          <p className="text-center text-red-400 mt-3 text-sm px-4">{error}</p>
        )}

        <div className="mt-6 w-full max-w-xs">
          {/* The verification happens via email link, no code input needed */}
          {/* <input type="text" placeholder="6 digit code" className="w-full p-3 bg-white text-black rounded-lg focus:outline-none" /> */}
          {/* <button type='button' onClick={() => navigate('/dashboard')} className="...">Verify</button> */}

          {isVerifying && !error && (
            <p className="text-center text-gray-400 mt-4 text-xs">
              Checking verification status...
            </p>
          )}
        </div>

        <div className="mt-5 text-center text-sm">
          Didn't receive the email?
          <button
            onClick={handleResendEmail}
            disabled={!canResend}
            className={`ml-2 font-semibold ${
              canResend
                ? "text-[#C99F4A] hover:text-yellow-500 cursor-pointer"
                : "text-gray-500 cursor-not-allowed"
            }`}
          >
            Resend Link {timer > 0 ? `(${timer}s)` : ""}
          </button>
        </div>

        {/* Optional: Add a link to manually go back or contact support */}
        <p className="text-center text-gray-400 cursor-pointer mt-8 text-xs">
          If you encounter issues, try{" "}
          <Link
            to={localStorage.getItem("userType") === "client" ? "/portal/ClientSignIn" : "/portal/LawyerSignIn"}
            className="text-[#C99F4A] underline"
          >
            logging in
          </Link>{" "}
          again or contact support.
        </p>
      </div>
    </div>
  );
}
